/**
 * jc-reviews.js - the review window (LICHT-BESLUITEN L37).
 *
 * Thomas 09-10-2026: "alle kersttruien-reviews van Bol ... net zoals bij Smooche
 * z'n pop-up, maar dan met alle reviews", then: "ik wil een totaliteit hebben van
 * de reviews ... En als klanten erop klikken zien ze gewoon alles, het gaat
 * namelijk allemaal over kersttruien", in German and English too.
 *
 * A click on a trigger ([data-jc-reviews-open]: the rating line above the title
 * and the shop rating) opens a dialog with every review of our sweaters:
 * - the summary: what bol.com itself counts for our sweaters (the same total as
 *   the rating line), its average and a bar per star; each bar filters the list;
 * - the list: every review with text that Cortex has from bol.com, newest first
 *   by default, each once, every star; "load more" pages through it. A card is
 *   compact (Thomas 09-10): stars, title, text and the date (no name or place:
 *   "dat doet er allemaal niet toe"), and top right
 *   a small round photo of the sweater it is about, linking to that sweater;
 * - under the list one small link "Over onze reviews" to the FAQ answer on
 *   where the reviews come from and how we collect them (Omnibus).
 * On /de and /en a review shows Cortex's machine translation with "Automatisch
 * vertaald" and a button for the original; the original is never replaced.
 *
 * All numbers and texts come from one Cortex document,
 * /apps/cortex/public/reviews/<winkel_id>?soort=kersttruien, first-party through
 * the app proxy. No answer: an error with a retry, never a made-up number.
 *
 * Accessibility: a native <dialog> opened with showModal() (the rest of the
 * page is inert, Escape closes it), labelled by its heading; focus goes to the
 * close button and back to the trigger on close. The bars are toggle buttons
 * (aria-pressed), the list count is announced in a live region, the
 * original/translation switch is a toggle button too.
 *
 * Config: <script type="application/json" data-jc-reviews-config> from
 * snippets/jc-reviews.liquid (winkel id, endpoint, language, sentences).
 */
(() => {
  if (window.jcReviews) return;

  const configEl = document.querySelector('[data-jc-reviews-config]');
  if (!configEl) return;
  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }
  const t = config.strings || {};
  const SORTS = [
    ['nieuwste', t.sortNewest],
    ['hoogste', t.sortHighest],
    ['laagste', t.sortLowest],
    ['oudste', t.sortOldest],
  ];
  const STAR_PATH = 'M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6L2.5 9.4l6.6-.8z';

  const fill = (text, values) =>
    String(text || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  const number = (value) => new Intl.NumberFormat(config.locale).format(value);
  const decimal = (value) =>
    new Intl.NumberFormat(config.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
  // Cortex names colours in Dutch ("Rood/Wit/Blauw"); de and en have a word list.
  const colourName = (colour) =>
    colour ? colour.split('/').map((part) => (t.colours && t.colours[part.trim()]) || part).join('/') : colour;
  const countText = (count) => (count === 1 ? t.countOne : fill(t.countOther, { count: number(count) }));
  const starText = (stars) => (stars === 1 ? t.starOne : fill(t.starOther, { stars }));
  const dateText = (iso) => {
    if (!iso) return '';
    const [year, month, day] = iso.split('-').map(Number);
    return new Intl.DateTimeFormat(config.locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(
      new Date(Date.UTC(year, month - 1, day))
    );
  };

  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (value === null || value === undefined || value === false) continue;
      if (key === 'class') node.className = value;
      else if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value === true ? '' : value);
    }
    for (const child of [].concat(children)) if (child) node.append(child);
    return node;
  }

  /** Five stars; `rating` may be a decimal (the average) or a whole star. */
  function stars(rating, size, label) {
    const wrap = el('span', { class: 'jc-reviews__stars', role: label ? 'img' : null, 'aria-label': label || null, 'aria-hidden': label ? null : 'true' });
    for (let i = 1; i <= 5; i += 1) {
      const share = Math.max(0, Math.min(1, rating - (i - 1)));
      const star = el('span', { class: 'jc-reviews__star' });
      star.style.setProperty('--share', `${Math.round(share * 100)}%`);
      star.innerHTML = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${STAR_PATH}"/></svg><svg class="jc-reviews__star-on" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${STAR_PATH}"/></svg>`;
      wrap.append(star);
    }
    return wrap;
  }

  // ── The sweater a review is about: a small round photo, linking to it ──

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const photos = new Map();

  /**
   * The product photo of a shop product, as snippets/jc-card-photos.liquid picks
   * it: the first image whose file name says _front (or _font_, a typo on five
   * listings), otherwise the featured image. One request per product, shared.
   */
  function photoOf(handle) {
    if (!photos.has(handle)) {
      photos.set(
        handle,
        // Without the language prefix: /de/products/<handle>.js is a 404 when the
        // German handle is translated; the page link below redirects instead.
        fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((product) => {
            if (!product) return null;
            const images = (product.images || []).filter(Boolean);
            const src = images.find((image) => /_front|_font_/i.test(image)) || product.featured_image || images[0];
            if (!src) return null;
            const url = src.startsWith('//') ? `https:${src}` : src;
            return `${url}${url.includes('?') ? '&' : '?'}width=96`;
          })
          .catch(() => null)
      );
    }
    return photos.get(handle);
  }

  function productPhoto(review) {
    const handle = review.product && review.product.handle;
    if (!handle) return null;
    const about = review.geschreven_bij
      ? [review.geschreven_bij.ontwerp, colourName(review.geschreven_bij.kleur)].filter(Boolean).join(', ')
      : '';
    const link = el('a', {
      class: 'jc-reviews__photo',
      href: `${ROOT}products/${encodeURIComponent(handle)}`,
      'aria-label': fill(t.viewProduct, { product: about || handle }),
    });
    const image = el('img', { alt: '', width: '40', height: '40', loading: 'lazy', decoding: 'async' });
    link.append(image);
    photoOf(handle).then((src) => {
      if (src) image.src = src;
      else link.remove();
    });
    return link;
  }

  class ReviewWindow {
    constructor() {
      this.state = { sterren: null, sorteer: 'nieuwste', page: 0, loaded: false };
      this.trigger = null;
      this.build();
    }

    build() {
      this.close = el('button', { type: 'button', class: 'jc-reviews__close', 'aria-label': t.close });
      this.close.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
      this.title = el('h2', { class: 'jc-reviews__title', id: 'jc-reviews-title', text: t.title });
      this.scope = el('p', { class: 'jc-reviews__scope', text: t.scope });
      this.score = el('div', { class: 'jc-reviews__score' });
      this.bars = el('ul', { class: 'jc-reviews__bars', role: 'list', 'aria-label': t.filterLabel });
      this.showAll = el('button', { type: 'button', class: 'jc-reviews__show-all', text: t.showAll, hidden: true });
      this.sort = el('select', { class: 'jc-reviews__sort', id: 'jc-reviews-sort' },
        SORTS.map(([value, label]) => el('option', { value, text: label })));
      this.status = el('p', { class: 'jc-reviews__status', 'aria-live': 'polite' });
      this.list = el('ol', { class: 'jc-reviews__list', role: 'list' });
      this.shown = el('p', { class: 'jc-reviews__shown' });
      this.more = el('button', { type: 'button', class: 'jc-reviews__more btn btn--secondary', text: t.loadMore, hidden: true });
      this.retry = el('button', { type: 'button', class: 'jc-reviews__retry btn btn--secondary', text: t.retry, hidden: true });

      const summary = el('aside', { class: 'jc-reviews__summary' }, [
        this.score,
        this.bars,
        this.showAll,
      ]);
      const main = el('section', { class: 'jc-reviews__main', 'aria-labelledby': 'jc-reviews-title' }, [
        el('div', { class: 'jc-reviews__tools' }, [
          el('label', { class: 'jc-reviews__sort-label', for: 'jc-reviews-sort', text: t.sortLabel }),
          this.sort,
        ]),
        this.status,
        this.list,
        el('div', { class: 'jc-reviews__footer' }, [
          this.shown,
          this.more,
          this.retry,
          config.aboutUrl ? el('a', { class: 'jc-reviews__about', href: config.aboutUrl, text: t.about }) : null,
        ]),
      ]);
      this.body = el('div', { class: 'jc-reviews__body' }, [summary, main]);
      this.dialog = el('dialog', { class: 'jc-reviews', 'aria-labelledby': 'jc-reviews-title', 'data-jc-reviews-dialog': true }, [
        el('div', { class: 'jc-reviews__panel' }, [
          el('header', { class: 'jc-reviews__bar' }, [
            this.close,
            el('div', { class: 'jc-reviews__heading' }, [this.title, this.scope]),
          ]),
          this.body,
        ]),
      ]);
      document.body.append(this.dialog);

      this.close.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => this.onClose());
      // A click on the backdrop (outside the panel) closes too.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.sort.addEventListener('change', () => this.reload({ sorteer: this.sort.value }));
      this.showAll.addEventListener('click', () => this.reload({ sterren: null }));
      this.more.addEventListener('click', () => this.load(this.state.page + 1));
      this.retry.addEventListener('click', () => this.load(this.state.page || 1));
    }

    open(trigger) {
      this.trigger = trigger;
      // One window for the whole shop: what was loaded stays (filter, order, pages).
      if (!this.state.loaded) {
        this.state.loaded = true;
        this.load(1);
      }
      document.documentElement.classList.add('jc-reviews-open');
      this.dialog.showModal();
      this.body.scrollTop = 0;
      this.close.focus();
    }

    onClose() {
      document.documentElement.classList.remove('jc-reviews-open');
      if (this.trigger && this.trigger.isConnected) this.trigger.focus();
    }

    reset() {
      this.list.replaceChildren();
      this.shown.textContent = '';
      this.more.hidden = true;
      this.retry.hidden = true;
    }

    reload(change) {
      this.state = { ...this.state, ...change, page: 0 };
      this.reset();
      this.load(1);
    }

    url(page) {
      const params = new URLSearchParams({
        soort: 'kersttruien',
        taal: config.language || 'nl',
        pagina: String(page),
        per_pagina: String(config.perPage || 10),
        sorteer: this.state.sorteer,
      });
      if (this.state.sterren) params.set('sterren', String(this.state.sterren));
      return `${config.endpoint}${encodeURIComponent(config.shopId)}?${params}`;
    }

    async load(page) {
      const state = this.state;
      this.more.disabled = true;
      this.retry.hidden = true;
      this.status.textContent = t.loading;
      this.dialog.setAttribute('aria-busy', 'true');
      let data = null;
      try {
        const response = await fetch(this.url(page), { headers: { Accept: 'application/json' }, credentials: 'omit' });
        if (response.ok) data = await response.json();
      } catch (error) {
        data = null;
      }
      if (state !== this.state) return; // a newer filter or order took over
      this.dialog.removeAttribute('aria-busy');
      this.more.disabled = false;
      if (!data || !Array.isArray(data.reviews)) {
        this.status.textContent = t.error;
        this.retry.hidden = false;
        this.more.hidden = true;
        return;
      }
      state.page = data.pagina;
      this.renderSummary(data);
      for (const review of data.reviews) this.list.append(this.card(review));
      const shown = this.list.children.length;
      const total = data.aantal_gefilterd;
      this.shown.textContent = total ? fill(t.shown, { shown: number(shown), total: number(total) }) : '';
      this.more.hidden = shown >= total;
      if (!data.totaal) this.status.textContent = t.empty;
      else if (!total) this.status.textContent = fill(t.emptyFilter, { stars: starText(state.sterren) });
      else if (state.sterren && page === 1) this.status.textContent = fill(t.filtered, { count: number(total), stars: starText(state.sterren) });
      else this.status.textContent = page === 1 ? '' : this.shown.textContent;
    }

    /**
     * The summary is bol.com's own count when Cortex has it (the number on the
     * rating line), otherwise the reviews Cortex has. The bars filter the list,
     * so a bar with no review text to show is off.
     */
    renderSummary(data) {
      const bol = data.bol && data.bol.totaal ? data.bol : null;
      const total = bol ? bol.totaal : data.totaal;
      const average = bol ? bol.gemiddelde : data.gemiddelde;
      const spread = bol ? bol.verdeling : data.verdeling;
      const withText = data.verdeling || {};

      this.score.replaceChildren();
      if (total) {
        const shown = decimal(average);
        this.score.append(
          el('span', { class: 'jc-reviews__average', text: shown, 'aria-hidden': 'true' }),
          el('span', { class: 'jc-reviews__score-side' }, [
            stars(average, 18, fill(t.average, { rating: shown })),
            el('span', { class: 'jc-reviews__count', text: countText(total) }),
          ])
        );
      }

      this.bars.replaceChildren();
      for (const star of [5, 4, 3, 2, 1]) {
        const count = (spread || {})[String(star)] || 0;
        const share = total ? Math.round((count / total) * 1000) / 10 : 0;
        const pressed = this.state.sterren === star;
        const button = el('button', {
          type: 'button',
          class: 'jc-reviews__bar-button',
          'aria-pressed': pressed ? 'true' : 'false',
          'aria-label': `${starText(star)}: ${countText(count)}`,
          disabled: !withText[String(star)],
          'data-stars': star,
        }, [
          el('span', { class: 'jc-reviews__bar-stars', 'aria-hidden': 'true' }, [stars(star, 12)]),
          el('span', { class: 'jc-reviews__bar-track', 'aria-hidden': 'true' }, [el('span', { class: 'jc-reviews__bar-fill' })]),
          el('span', { class: 'jc-reviews__bar-count', 'aria-hidden': 'true', text: number(count) }),
        ]);
        button.querySelector('.jc-reviews__bar-fill').style.width = `${share}%`;
        button.addEventListener('click', () => this.reload({ sterren: pressed ? null : star }));
        this.bars.append(el('li', {}, [button]));
      }
      this.showAll.hidden = !this.state.sterren;
    }

    card(review) {
      const meta = review.datum
        ? el('p', { class: 'jc-reviews__meta' }, [el('time', { datetime: review.datum, text: dateText(review.datum) })])
        : null;
      // Cortex sends the translation in de and en; the card shows it without a
      // note (Thomas 10-10: "Automatically translated" is unnecessary).
      const translation = review.vertaling;
      const title = translation ? translation.titel || review.titel : review.titel;
      const lang = translation ? config.language : 'nl';

      return el('li', { class: 'jc-reviews__card', 'data-stars': review.sterren, 'data-id': review.id }, [
        el('article', {}, [
          el('div', { class: 'jc-reviews__card-top' }, [
            stars(review.sterren, 14, fill(t.average, { rating: review.sterren })),
            productPhoto(review),
          ]),
          title ? el('h3', { class: 'jc-reviews__card-title', lang, text: title }) : null,
          el('p', { class: 'jc-reviews__text', lang, text: translation ? translation.tekst : review.tekst }),
          meta,
        ]),
      ]);
    }
  }

  let windowInstance = null;
  window.jcReviews = {
    open(trigger) {
      if (!windowInstance) windowInstance = new ReviewWindow();
      windowInstance.open(trigger);
    },
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-jc-reviews-open]');
    if (!trigger) return;
    event.preventDefault();
    window.jcReviews.open(trigger);
  });
  // Until this runs the triggers read as plain text (jc-reviews.css); delegation
  // above also covers triggers that a section re-render puts back later.
  document.documentElement.classList.add('jc-reviews-ready');
})();
